import type { BuildingZone, PlayerRace } from '@kaeliux/shared';

/**
 * Race-styled ground-clutter prop art (DS-29). Sourced from
 * assets/images/{race}/terrain/*.png — one folder per race, sibling to the
 * existing assets/images/{race}/buildings/ convention. See that folder's
 * SOURCES.md for how the art was made.
 *
 * Deliberately dynamic (`import.meta.glob`), not a hand-maintained list of
 * imports: whatever files exist in a race's `terrain/` folder at build time
 * are what that race scatters. A race with no `terrain/` folder at all would
 * get zero props rather than a placeholder or an error — nothing to add here
 * if that ever happens again, just drop the files in.
 *
 * File naming carries no semantic meaning (`1-1.png`, `2-3.png`, ... — a
 * sprite sheet's row-column position) and isn't assumed to line up between
 * races, so every discovered prop is treated as one interchangeable pool
 * per race, used the same way in all three zones, rather than curated
 * per-zone lists keyed to specific filenames.
 */
/** Only the sheet-position files, so the full `<race>_terrain.png` reference sheet in the same folder is never bundled. */
const TERRAIN_MODULES = import.meta.glob<string>('../assets/images/*/terrain/[0-9]*-[0-9]*.png', {
    eager: true,
    import: 'default',
});

/** Extracts race and sheet position (`1-1`) from a prop file path. */
const PROP_FILENAME = /\/([a-z]+)\/terrain\/(\d+-\d+)\.png$/;

function emptyByRace(): Record<PlayerRace, Record<string, string>> {
    return { humans: {}, robots: {}, axons: {} };
}

function buildPropAssets(): Record<PlayerRace, Record<string, string>> {
    const byRace = emptyByRace();
    for (const [path, src] of Object.entries(TERRAIN_MODULES)) {
        const match = PROP_FILENAME.exec(path);
        if (!match) continue;
        const race = match[1] as PlayerRace;
        const kind = match[2] as string;
        if (!(race in byRace)) continue; // an unrecognised folder name under assets/images/*/terrain/ — ignore rather than crash
        byRace[race][kind] = src;
    }
    return byRace;
}

/** `SURFACE_PROP_ASSETS[race][kind]` -> the prop's image URL. */
export const SURFACE_PROP_ASSETS: Record<PlayerRace, Record<string, string>> = buildPropAssets();

function buildKindsByZone(): Record<PlayerRace, Record<BuildingZone, readonly string[]>> {
    const result = {} as Record<PlayerRace, Record<BuildingZone, readonly string[]>>;
    for (const race of Object.keys(SURFACE_PROP_ASSETS) as PlayerRace[]) {
        const kinds = Object.keys(SURFACE_PROP_ASSETS[race]);
        result[race] = { resource: kinds, industry: kinds, military: kinds };
    }
    return result;
}

/** Which prop kinds are eligible in each zone, per race — see the file header on why this is one flat pool per race rather than a curated per-zone split. */
export const SURFACE_PROP_KINDS_BY_ZONE: Record<PlayerRace, Record<BuildingZone, readonly string[]>> = buildKindsByZone();

